import React, { useState, useEffect, useCallback, useRef } from 'react';

const API = process.env.REACT_APP_API_URL || 'https://aizhi.fyi';

function Reader({ book, onBack }) {
  const [chunk, setChunk] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fontSize, setFontSize] = useState(16);
  const [darkMode, setDarkMode] = useState(false);
  const [showToc, setShowToc] = useState(false);
  const [toc, setToc] = useState([]);
  const [progress, setProgress] = useState({ zhizhi: 0, aiden: 0 });
  const [noteText, setNoteText] = useState('');
  const [selectedText, setSelectedText] = useState('');
  const [selectionRange, setSelectionRange] = useState(null);
  const [showNoteInput, setShowNoteInput] = useState(false);
  const contentRef = useRef();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  // 讨论系统
  const [showDiscuss, setShowDiscuss] = useState(false);
  const [discussions, setDiscussions] = useState([]);
  const [discussInput, setDiscussInput] = useState('');
  const [discussSending, setDiscussSending] = useState(false);
  const [aidenReading, setAidenReading] = useState(false);
  const discussEndRef = useRef(null);
  const discussInputRef = useRef(null);

  var loadChunk = useCallback(async function(index) {
    setLoading(true);
    try {
      var res = await fetch(API + '/api/books/' + book.id + '/chunks/' + index);
      var data = await res.json();
      setChunk(data.chunk);
      setAnnotations(data.annotations);
      setCurrentIndex(index);
      // 更新知枝的阅读进度
      fetch(API + '/api/books/' + book.id + '/progress', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reader: 'zhizhi', current_chunk: index })
      });
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [book.id]);

  useEffect(function() {
    // 加载目录和进度
    fetch(API + '/api/books/' + book.id + '/toc').then(r => r.json()).then(setToc).catch(() => {});
    fetch(API + '/api/books/' + book.id + '/progress').then(r => r.json()).then(function(data) {
      var p = { zhizhi: 0, aiden: 0 };
      (data || []).forEach(function(d) { p[d.reader] = d.current_chunk; });
      setProgress(p);
      loadChunk(p.zhizhi || 0);
    }).catch(() => loadChunk(0));
  }, [book.id, loadChunk]);

  function handlePrev() { if (currentIndex > 0) loadChunk(currentIndex - 1); }
  function handleNext() { if (currentIndex < book.total_chunks - 1) loadChunk(currentIndex + 1); }

  // 加载讨论历史
  async function loadDiscussions(chunkIdx) {
    try {
      var res = await fetch(API + '/api/books/' + book.id + '/discussions?chunk_index=' + (chunkIdx !== undefined ? chunkIdx : currentIndex));
      var data = await res.json();
      setDiscussions(Array.isArray(data) ? data : []);
    } catch (e) { console.error('load discussions error:', e); }
  }

  // 发送讨论消息
  async function handleSendDiscuss() {
    if (!discussInput.trim() || discussSending) return;
    setDiscussSending(true);
    var msg = discussInput.trim();
    setDiscussInput('');
    // 乐观更新：先显示知枝的消息
    var tmpMsg = { id: 'tmp-' + Date.now(), sender: 'zhizhi', message: msg, created_at: new Date().toISOString() };
    setDiscussions(function(prev) { return prev.concat([tmpMsg]); });
    try {
      var body = { chunk_index: currentIndex, message: msg };
      if (selectedText) body.selected_text = selectedText;
      var res = await fetch(API + '/api/books/' + book.id + '/discuss', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      var data = await res.json();
      if (data.reply) {
        // 用实际数据替换
        await loadDiscussions(currentIndex);
      }
    } catch (e) { console.error('discuss error:', e); }
    setDiscussSending(false);
  }

  // 让哥哥读当前章节
  async function handleAidenRead() {
    if (aidenReading) return;
    setAidenReading(true);
    try {
      var res = await fetch(API + '/api/books/' + book.id + '/aiden-read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chunk_index: currentIndex })
      });
      var data = await res.json();
      if (data.already_read) {
        // 已经读过了，不需要做什么
      } else if (data.success && data.annotations) {
        // 刷新当前页的批注
        setAnnotations(function(prev) { return prev.concat(data.annotations); });
      }
    } catch (e) { console.error('aiden read error:', e); }
    setAidenReading(false);
  }

  // 打开讨论面板时加载历史
  useEffect(function() {
    if (showDiscuss) {
      loadDiscussions(currentIndex);
    }
  }, [showDiscuss, currentIndex]);

  // 讨论面板滚动到底部
  useEffect(function() {
    if (discussEndRef.current && showDiscuss) {
      discussEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [discussions, showDiscuss]);

  function handleTextSelect() {
    var sel = window.getSelection();
    var text = sel.toString().trim();
    if (text && text.length > 0 && contentRef.current) {
      setSelectedText(text);
      // 计算在chunk content中的偏移
      try {
        var range = sel.getRangeAt(0);
        var preRange = document.createRange();
        preRange.selectNodeContents(contentRef.current);
        preRange.setEnd(range.startContainer, range.startOffset);
        var startOffset = preRange.toString().length;
        setSelectionRange({ start: startOffset, end: startOffset + text.length });
      } catch (e) {
        setSelectionRange(null);
      }
    }
  }

  async function handleHighlight() {
    if (!selectedText || !selectionRange) return;
    try {
      var res = await fetch(API + '/api/books/' + book.id + '/annotations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chunk_index: currentIndex,
          author: 'zhizhi',
          type: 'highlight',
          selected_text: selectedText,
          start_offset: selectionRange.start,
          end_offset: selectionRange.end
        })
      });
      var data = await res.json();
      if (data.id) {
        setAnnotations(prev => [...prev, data]);
        setSelectedText('');
        setSelectionRange(null);
        window.getSelection().removeAllRanges();
      }
    } catch (e) { console.error(e); }
  }

  async function handleAddNote() {
    if (!noteText.trim()) return;
    try {
      var body = {
        chunk_index: currentIndex,
        author: 'zhizhi',
        type: 'note',
        content: noteText.trim()
      };
      if (selectedText && selectionRange) {
        body.selected_text = selectedText;
        body.start_offset = selectionRange.start;
        body.end_offset = selectionRange.end;
      }
      var res = await fetch(API + '/api/books/' + book.id + '/annotations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      var data = await res.json();
      if (data.id) {
        setAnnotations(prev => [...prev, data]);
        setNoteText('');
        setSelectedText('');
        setSelectionRange(null);
        setShowNoteInput(false);
      }
    } catch (e) { console.error(e); }
  }

  async function handleDeleteAnnotation(id) {
    try {
      await fetch(API + '/api/books/' + book.id + '/annotations/' + id, { method: 'DELETE' });
      setAnnotations(prev => prev.filter(a => a.id !== id));
    } catch (e) { console.error(e); }
  }

  async function handleSearch() {
    if (!searchQuery.trim()) return;
    try {
      var res = await fetch(API + '/api/books/' + book.id + '/search?q=' + encodeURIComponent(searchQuery));
      var data = await res.json();
      setSearchResults(data);
    } catch (e) { console.error(e); }
  }

  // 批注弹窗
  const [popupAnnotation, setPopupAnnotation] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const longPressTimer = useRef(null);

  function handleHighlightPress(h, e) {
    // 找关联的note批注
    var relatedNote = annotations.find(function(a) {
      return a.type === 'note' && a.selected_text && a.start_offset === h.start_offset && a.end_offset === h.end_offset;
    });
    // 也找独立的note（通过selected_text匹配）
    if (!relatedNote) {
      var hlText = chunk.content.slice(h.start_offset, h.end_offset);
      relatedNote = annotations.find(function(a) {
        return a.type === 'note' && a.selected_text && a.selected_text === hlText;
      });
    }
    setPopupAnnotation({
      highlight: h,
      note: relatedNote,
      author: h.author,
      text: chunk.content.slice(h.start_offset, h.end_offset)
    });
  }

  function handleHighlightTouchStart(h, e) {
    longPressTimer.current = setTimeout(function() {
      handleHighlightPress(h, e);
    }, 500);
  }

  function handleHighlightTouchEnd() {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }

  // 渲染带高亮的文本
  function renderContent() {
    if (!chunk) return null;
    var text = chunk.content;
    var highlights = annotations.filter(a => a.type === 'highlight' && a.start_offset !== undefined);

    if (highlights.length === 0) {
      return <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.9 }}>{text}</div>;
    }

    // 排序高亮区间
    highlights.sort(function(a, b) { return a.start_offset - b.start_offset; });

    var parts = [];
    var lastEnd = 0;

    highlights.forEach(function(h, i) {
      if (h.start_offset > lastEnd) {
        parts.push(<span key={'t' + i}>{text.slice(lastEnd, h.start_offset)}</span>);
      }
      var bgColor = h.author === 'zhizhi' ? 'rgba(255,138,128,0.25)' : 'rgba(128,203,196,0.25)';
      var borderColor = h.author === 'zhizhi' ? '#FF8A80' : '#80CBC4';
      parts.push(
        <span
          key={'h' + i}
          style={{
            backgroundColor: bgColor,
            borderBottom: '2px solid ' + borderColor,
            padding: '1px 0',
            borderRadius: 2,
            cursor: 'pointer'
          }}
          onTouchStart={function(e) { handleHighlightTouchStart(h, e); }}
          onTouchEnd={handleHighlightTouchEnd}
          onTouchCancel={handleHighlightTouchEnd}
          onDoubleClick={function(e) { e.preventDefault(); handleHighlightPress(h, e); }}
        >
          {text.slice(h.start_offset, h.end_offset)}
        </span>
      );
      lastEnd = h.end_offset;
    });

    if (lastEnd < text.length) {
      parts.push(<span key="end">{text.slice(lastEnd)}</span>);
    }

    return <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.9 }}>{parts}</div>;
  }

  var bg = darkMode ? '#1a1a1a' : '#faf8f5';
  var textColor = darkMode ? '#d4d0c8' : '#2d2418';
  var subColor = darkMode ? '#888' : '#8c7b6b';
  var cardBg = darkMode ? '#252525' : 'rgba(255,255,255,0.6)';

  var notes = annotations.filter(a => a.type === 'note');

  return (
    <div style={{ background: bg, minHeight: '100vh', color: textColor, transition: 'all 0.3s' }}>
      {/* 顶栏 */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 14px',
        paddingTop: 'max(10px, env(safe-area-inset-top))',
        background: darkMode ? 'rgba(26,26,26,0.92)' : 'rgba(250,248,245,0.92)',
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid ' + (darkMode ? '#333' : 'rgba(0,0,0,0.04)')
      }}>
        <div style={{ cursor: 'pointer', fontSize: 14, color: subColor, padding: '6px 8px', display: 'flex', alignItems: 'center', gap: 4 }} onClick={onBack}>
          <span style={{ fontSize: 16 }}>‹</span>
          <span>书架</span>
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, flex: 1, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '0 4px' }}>
          {book.title}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span
            onClick={handleAidenRead}
            style={{
              cursor: aidenReading ? 'wait' : 'pointer', fontSize: 13, padding: '4px 8px',
              background: aidenReading ? '#80CBC4' : (darkMode ? '#333' : '#f0ede8'),
              color: aidenReading ? '#fff' : subColor,
              borderRadius: 8, fontWeight: 500, transition: 'all 0.2s',
              opacity: aidenReading ? 0.8 : 1, whiteSpace: 'nowrap'
            }}
            title="让哥哥也读这一页"
          >{aidenReading ? '读中...' : '哥哥读'}</span>
          <span style={{ cursor: 'pointer', fontSize: 15, opacity: 0.55, padding: '4px' }} onClick={() => setShowSearch(!showSearch)}>🔍</span>
          <span style={{ cursor: 'pointer', fontSize: 15, opacity: 0.55, padding: '4px' }} onClick={() => setShowToc(!showToc)}>☰</span>
        </div>
      </div>

      {/* 搜索栏 */}
      {showSearch && (
        <div style={{ padding: '10px 14px', background: cardBg }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="搜索书中内容..."
              style={{
                flex: 1, padding: '8px 12px', border: 'none', borderRadius: 8,
                background: darkMode ? '#333' : '#f0ede8', color: textColor,
                fontSize: 14, outline: 'none'
              }}
            />
            <button onClick={handleSearch} style={{
              padding: '8px 14px', borderRadius: 8, border: 'none',
              background: '#80CBC4', color: '#fff', fontSize: 13, cursor: 'pointer'
            }}>搜索</button>
          </div>
          {searchResults.length > 0 && (
            <div style={{ marginTop: 8, maxHeight: 200, overflow: 'auto' }}>
              {searchResults.map(function(r, i) {
                return (
                  <div key={i} style={{
                    padding: '8px 10px', fontSize: 12, borderBottom: '1px solid ' + (darkMode ? '#333' : '#eee'),
                    cursor: 'pointer', opacity: 0.8
                  }} onClick={() => { loadChunk(r.chunk_index); setShowSearch(false); setSearchResults([]); }}>
                    <span style={{ fontWeight: 600, opacity: 0.6 }}>{r.title} </span>
                    {r.snippet}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 目录 */}
      {showToc && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 20,
          background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'flex-end'
        }} onClick={() => setShowToc(false)}>
          <div style={{
            width: '75%', maxWidth: 320, background: bg, height: '100%',
            overflow: 'auto', padding: '20px 16px'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>目录</h3>
            {toc.map(function(item) {
              var isCurrent = item.chunk_index === currentIndex;
              return (
                <div key={item.chunk_index} style={{
                  padding: '10px 12px', fontSize: 13, borderRadius: 8, marginBottom: 2,
                  background: isCurrent ? (darkMode ? '#333' : '#f0ede8') : 'transparent',
                  fontWeight: isCurrent ? 600 : 400,
                  cursor: 'pointer',
                  color: textColor
                }} onClick={() => { loadChunk(item.chunk_index); setShowToc(false); }}>
                  {item.title || ('第 ' + (item.chunk_index + 1) + ' 页')}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 正文 */}
      <div style={{ padding: '20px 18px 120px', maxWidth: 640, margin: '0 auto' }}>
        {/* 章节标题 */}
        {chunk && chunk.title && (
          <h3 style={{ fontSize: fontSize + 4, fontWeight: 600, margin: '0 0 20px', lineHeight: 1.4 }}>
            {chunk.title}
          </h3>
        )}

        {loading ? (
          <p style={{ textAlign: 'center', opacity: 0.5, marginTop: 60 }}>加载中...</p>
        ) : (
          <div
            ref={contentRef}
            onMouseUp={handleTextSelect}
            onTouchEnd={handleTextSelect}
            style={{ fontSize: fontSize, lineHeight: 1.9, letterSpacing: 0.3 }}
          >
            {renderContent()}
          </div>
        )}

        {/* 批注弹窗（长按/双击划线时显示） */}
        {popupAnnotation && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.3)', zIndex: 25,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20
          }} onClick={() => { setPopupAnnotation(null); setConfirmDelete(false); }}>
            <div style={{
              background: darkMode ? '#2a2a2a' : '#fff',
              borderRadius: 16, padding: '20px 18px', maxWidth: 320, width: '100%',
              boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
              position: 'relative'
            }} onClick={function(e) { e.stopPropagation(); }}>
              {/* 右上角关闭叉号 */}
              <span
                onClick={() => { setPopupAnnotation(null); setConfirmDelete(false); }}
                style={{
                  position: 'absolute', top: 12, right: 14,
                  fontSize: 18, color: subColor, cursor: 'pointer',
                  padding: '2px 6px', lineHeight: 1
                }}
              >✕</span>
              {/* 划线的人 */}
              <div style={{
                fontSize: 12, fontWeight: 600, marginBottom: 10,
                color: popupAnnotation.author === 'zhizhi' ? '#FF8A80' : '#80CBC4'
              }}>
                {popupAnnotation.author === 'zhizhi' ? '🐱 知枝的划线' : '🖤 林晏的划线'}
              </div>
              {/* 划线的文字 */}
              <div style={{
                fontSize: 14, lineHeight: 1.7, color: textColor,
                padding: '10px 12px', borderRadius: 10,
                background: darkMode ? '#333' : '#f8f6f3',
                borderLeft: '3px solid ' + (popupAnnotation.author === 'zhizhi' ? '#FF8A80' : '#80CBC4'),
                marginBottom: popupAnnotation.note ? 12 : 16
              }}>
                「{popupAnnotation.text.length > 100 ? popupAnnotation.text.substring(0, 100) + '...' : popupAnnotation.text}」
              </div>
              {/* 批注内容 */}
              {popupAnnotation.note && (
                <div style={{
                  fontSize: 13, lineHeight: 1.6, color: textColor,
                  padding: '10px 12px', borderRadius: 10,
                  background: darkMode ? '#383838' : '#faf8f5',
                  marginBottom: 16
                }}>
                  <div style={{ fontSize: 11, opacity: 0.5, marginBottom: 4 }}>
                    {popupAnnotation.note.author === 'zhizhi' ? '🐱 知枝的批注' : '🖤 林晏的批注'}
                  </div>
                  {popupAnnotation.note.content}
                </div>
              )}
              {!popupAnnotation.note && (
                <div style={{ fontSize: 12, color: subColor, fontStyle: 'italic', marginBottom: 16 }}>
                  暂无批注
                </div>
              )}
              {/* 删除按钮（知枝和林晏的都能删） */}
              <div style={{ marginTop: 4 }}>
                  {!confirmDelete ? (
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <span
                        onClick={function() { setConfirmDelete(true); }}
                        style={{
                          fontSize: 13, color: '#E57373', cursor: 'pointer',
                          padding: '6px 10px', borderRadius: 8,
                          background: darkMode ? '#3a2525' : '#fef2f2',
                          display: 'flex', alignItems: 'center', gap: 4
                        }}
                      >✕ 删除</span>
                    </div>
                  ) : (
                    <div style={{
                      padding: '12px 14px', borderRadius: 10,
                      background: darkMode ? '#3a2020' : '#fff5f5',
                      border: '1px solid ' + (darkMode ? '#5a3030' : '#FFCDD2')
                    }}>
                      <div style={{ fontSize: 13, color: '#E57373', marginBottom: 10, fontWeight: 500 }}>
                        确定删除这条划线吗？
                      </div>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button onClick={function() { setConfirmDelete(false); }} style={{
                          padding: '7px 14px', borderRadius: 8, border: 'none',
                          background: darkMode ? '#444' : '#f0ede8', color: textColor,
                          fontSize: 12, cursor: 'pointer'
                        }}>取消</button>
                        <button onClick={function() {
                          handleDeleteAnnotation(popupAnnotation.highlight.id);
                          if (popupAnnotation.note) handleDeleteAnnotation(popupAnnotation.note.id);
                          setPopupAnnotation(null);
                          setConfirmDelete(false);
                        }} style={{
                          padding: '7px 14px', borderRadius: 8, border: 'none',
                          background: '#E57373', color: '#fff',
                          fontSize: 12, cursor: 'pointer'
                        }}>确定删除</button>
                      </div>
                    </div>
                  )}
                </div>
            </div>
          </div>
        )}

        {/* 选中文本操作 */}
        {selectedText && (
          <div style={{
            position: 'fixed', bottom: 140, left: '50%', transform: 'translateX(-50%)',
            display: 'flex', gap: 8, padding: '8px 12px', borderRadius: 20,
            background: darkMode ? '#333' : '#fff',
            boxShadow: '0 2px 12px rgba(0,0,0,0.15)', zIndex: 15
          }}>
            <button onClick={handleHighlight} style={{
              padding: '6px 14px', borderRadius: 14, border: 'none',
              background: '#FF8A80', color: '#fff', fontSize: 12, cursor: 'pointer'
            }}>划线</button>
            <button onClick={() => setShowNoteInput(true)} style={{
              padding: '6px 14px', borderRadius: 14, border: 'none',
              background: '#80CBC4', color: '#fff', fontSize: 12, cursor: 'pointer'
            }}>批注</button>
            <button onClick={() => { setSelectedText(''); setSelectionRange(null); window.getSelection().removeAllRanges(); }} style={{
              padding: '6px 14px', borderRadius: 14, border: 'none',
              background: darkMode ? '#555' : '#e0ddd8', color: textColor, fontSize: 12, cursor: 'pointer', opacity: 0.7
            }}>取消</button>
          </div>
        )}

        {/* 批注输入 */}
        {showNoteInput && (
          <div style={{
            position: 'fixed', bottom: 140, left: 14, right: 14, zIndex: 15,
            padding: 14, borderRadius: 14,
            background: darkMode ? '#333' : '#fff',
            boxShadow: '0 2px 16px rgba(0,0,0,0.15)'
          }}>
            {selectedText && (
              <div style={{ fontSize: 12, opacity: 0.5, marginBottom: 8, fontStyle: 'italic' }}>
                「{selectedText.length > 40 ? selectedText.slice(0, 40) + '...' : selectedText}」
              </div>
            )}
            <textarea
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="写下你的想法..."
              style={{
                width: '100%', minHeight: 60, border: 'none', borderRadius: 8,
                background: darkMode ? '#444' : '#f8f6f3', color: textColor,
                padding: 10, fontSize: 13, outline: 'none', resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <button onClick={() => { setShowNoteInput(false); setNoteText(''); }} style={{
                padding: '6px 14px', borderRadius: 10, border: 'none',
                background: darkMode ? '#555' : '#e0ddd8', color: textColor, fontSize: 12, cursor: 'pointer'
              }}>取消</button>
              <button onClick={handleAddNote} style={{
                padding: '6px 14px', borderRadius: 10, border: 'none',
                background: '#FF8A80', color: '#fff', fontSize: 12, cursor: 'pointer'
              }}>保存</button>
            </div>
          </div>
        )}

        {/* 笔记区 */}
        {notes.length > 0 && (
          <div style={{ marginTop: 30, paddingTop: 20, borderTop: '1px solid ' + (darkMode ? '#333' : 'rgba(0,0,0,0.08)') }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, opacity: 0.6 }}>📝 批注</div>
            {notes.map(function(n) {
              var isZhizhi = n.author === 'zhizhi';
              var borderColor = isZhizhi ? '#FF8A80' : '#80CBC4';
              return (
                <div key={n.id} style={{
                  padding: '10px 12px', marginBottom: 8, borderRadius: 8,
                  borderLeft: '3px solid ' + borderColor,
                  background: darkMode ? '#252525' : 'rgba(255,255,255,0.5)',
                  fontSize: 13, position: 'relative'
                }}>
                  <div style={{ fontSize: 11, opacity: 0.5, marginBottom: 4 }}>
                    {isZhizhi ? '🐱 知枝' : '🖤 林晏'}
                    {n.selected_text && <span style={{ fontStyle: 'italic' }}> · 「{n.selected_text.slice(0, 30)}{n.selected_text.length > 30 ? '...' : ''}」</span>}
                  </div>
                  <div>{n.content}</div>
                  {isZhizhi && (
                    <span style={{
                      position: 'absolute', top: 8, right: 10, fontSize: 14,
                      opacity: 0.3, cursor: 'pointer'
                    }} onClick={() => handleDeleteAnnotation(n.id)}>×</span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* 进度对比 */}
        <div style={{ marginTop: 30, padding: '12px 14px', borderRadius: 10, background: cardBg, fontSize: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span>🐱 知枝读到第 {progress.zhizhi + 1} 页</span>
            <span>🖤 林晏读到第 {progress.aiden + 1} 页</span>
          </div>
          <div style={{ height: 4, borderRadius: 2, background: darkMode ? '#333' : '#e8e4df', overflow: 'hidden', position: 'relative' }}>
            <div style={{
              position: 'absolute', height: '100%', borderRadius: 2,
              width: ((progress.zhizhi + 1) / book.total_chunks * 100) + '%',
              background: '#FF8A80', opacity: 0.7
            }} />
            <div style={{
              position: 'absolute', height: '100%', borderRadius: 2,
              width: ((progress.aiden + 1) / book.total_chunks * 100) + '%',
              background: '#80CBC4', opacity: 0.7
            }} />
          </div>
        </div>
      </div>

      {/* 讨论浮动按钮 */}
      {!showDiscuss && (
        <div
          onClick={() => setShowDiscuss(true)}
          style={{
            position: 'fixed', bottom: 120, right: 16, zIndex: 12,
            width: 48, height: 48, borderRadius: '50%',
            background: '#80CBC4', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, cursor: 'pointer',
            boxShadow: '0 2px 12px rgba(128,203,196,0.4)',
            transition: 'transform 0.2s'
          }}
          title="讨论这一章"
        >💬</div>
      )}

      {/* 讨论面板 */}
      {showDiscuss && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 30,
          height: '55vh', display: 'flex', flexDirection: 'column',
          background: darkMode ? '#1e1e1e' : '#faf8f5',
          borderTop: '2px solid ' + (darkMode ? '#333' : '#80CBC4'),
          borderRadius: '16px 16px 0 0',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.1)'
        }}>
          {/* 讨论面板标题栏 */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid ' + (darkMode ? '#333' : 'rgba(0,0,0,0.06)')
          }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: textColor }}>
              💬 讨论 · {chunk && chunk.title ? chunk.title : ('第 ' + (currentIndex + 1) + ' 页')}
            </div>
            <span
              onClick={() => setShowDiscuss(false)}
              style={{ fontSize: 18, cursor: 'pointer', color: subColor, padding: '4px 8px' }}
            >✕</span>
          </div>

          {/* 消息列表 */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '12px 14px',
            WebkitOverflowScrolling: 'touch'
          }}>
            {discussions.length === 0 && (
              <div style={{ textAlign: 'center', padding: '30px 0', color: subColor, fontSize: 13 }}>
                还没有讨论，和哥哥聊聊这一章吧
              </div>
            )}
            {discussions.map(function(d) {
              var isMe = d.sender === 'zhizhi';
              return (
                <div key={d.id} style={{
                  display: 'flex',
                  justifyContent: isMe ? 'flex-end' : 'flex-start',
                  marginBottom: 10
                }}>
                  <div style={{
                    maxWidth: '78%',
                    padding: '10px 14px',
                    borderRadius: isMe ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                    background: isMe ? '#FF8A80' : (darkMode ? '#333' : '#fff'),
                    color: isMe ? '#fff' : textColor,
                    fontSize: 14, lineHeight: 1.6,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                  }}>
                    {d.selected_text && (
                      <div style={{
                        fontSize: 12, opacity: 0.7, fontStyle: 'italic',
                        marginBottom: 6, paddingBottom: 6,
                        borderBottom: '1px solid ' + (isMe ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.06)')
                      }}>
                        「{d.selected_text.length > 50 ? d.selected_text.substring(0, 50) + '...' : d.selected_text}」
                      </div>
                    )}
                    {d.message}
                  </div>
                </div>
              );
            })}
            {discussSending && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 10 }}>
                <div style={{
                  padding: '10px 14px', borderRadius: '14px 14px 14px 4px',
                  background: darkMode ? '#333' : '#fff', color: subColor,
                  fontSize: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                }}>哥哥正在想...</div>
              </div>
            )}
            <div ref={discussEndRef} />
          </div>

          {/* 选中的文本提示 */}
          {selectedText && (
            <div style={{
              padding: '6px 14px', fontSize: 12, color: subColor, fontStyle: 'italic',
              borderTop: '1px solid ' + (darkMode ? '#333' : 'rgba(0,0,0,0.04)'),
              background: darkMode ? '#252525' : '#f5f1ec'
            }}>
              选中：「{selectedText.length > 40 ? selectedText.substring(0, 40) + '...' : selectedText}」
              <span onClick={() => { setSelectedText(''); setSelectionRange(null); window.getSelection().removeAllRanges(); }}
                style={{ marginLeft: 8, cursor: 'pointer', opacity: 0.5 }}>✕</span>
            </div>
          )}

          {/* 输入框 */}
          <div style={{
            display: 'flex', gap: 8, padding: '10px 14px',
            paddingBottom: 'max(10px, env(safe-area-inset-bottom))',
            borderTop: '1px solid ' + (darkMode ? '#333' : 'rgba(0,0,0,0.04)'),
            background: darkMode ? '#1e1e1e' : '#faf8f5'
          }}>
            <input
              ref={discussInputRef}
              value={discussInput}
              onChange={function(e) { setDiscussInput(e.target.value); }}
              onKeyDown={function(e) { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendDiscuss(); } }}
              placeholder="和哥哥聊聊这一章..."
              disabled={discussSending}
              style={{
                flex: 1, minWidth: 0, width: 0,
                padding: '10px 14px', border: 'none', borderRadius: 12,
                background: darkMode ? '#333' : '#f0ede8', color: textColor,
                fontSize: 16, outline: 'none', fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            />
            <button
              onClick={handleSendDiscuss}
              disabled={discussSending || !discussInput.trim()}
              style={{
                padding: '10px 16px', borderRadius: 12, border: 'none',
                background: discussSending || !discussInput.trim() ? (darkMode ? '#333' : '#e0ddd8') : '#80CBC4',
                color: discussSending || !discussInput.trim() ? subColor : '#fff',
                fontSize: 14, fontWeight: 500, cursor: discussSending ? 'wait' : 'pointer',
                flexShrink: 0, transition: 'all 0.2s'
              }}
            >发送</button>
          </div>
        </div>
      )}

      {/* 底栏：翻页 + 设置（讨论面板打开时隐藏） */}
      {!showDiscuss && (
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10,
        padding: '10px 16px', paddingBottom: 'max(14px, env(safe-area-inset-bottom))',
        background: darkMode ? 'rgba(26,26,26,0.92)' : 'rgba(250,248,245,0.92)',
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        borderTop: '1px solid ' + (darkMode ? '#333' : 'rgba(0,0,0,0.04)')
      }}>
        {/* 翻页 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <button onClick={handlePrev} disabled={currentIndex === 0} style={{
            padding: '8px 22px', borderRadius: 10, border: 'none',
            background: currentIndex === 0 ? (darkMode ? '#252525' : '#ebe5dc') : '#80CBC4',
            color: currentIndex === 0 ? subColor : '#fff',
            fontSize: 13, fontWeight: 500, cursor: currentIndex === 0 ? 'default' : 'pointer',
            opacity: currentIndex === 0 ? 0.5 : 1
          }}>上一页</button>
          <span style={{ fontSize: 12, opacity: 0.45, fontVariantNumeric: 'tabular-nums' }}>{currentIndex + 1} / {book.total_chunks}</span>
          <button onClick={handleNext} disabled={currentIndex >= book.total_chunks - 1} style={{
            padding: '8px 22px', borderRadius: 10, border: 'none',
            background: currentIndex >= book.total_chunks - 1 ? (darkMode ? '#252525' : '#ebe5dc') : '#80CBC4',
            color: currentIndex >= book.total_chunks - 1 ? subColor : '#fff',
            fontSize: 13, fontWeight: 500, cursor: currentIndex >= book.total_chunks - 1 ? 'default' : 'pointer',
            opacity: currentIndex >= book.total_chunks - 1 ? 0.5 : 1
          }}>下一页</button>
        </div>

        {/* 设置 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18 }}>
          <span style={{ cursor: 'pointer', fontSize: 13, opacity: 0.5, padding: '4px', fontWeight: 500, color: textColor }} onClick={() => setFontSize(Math.max(12, fontSize - 1))}>A−</span>
          <span style={{ fontSize: 11, opacity: 0.35, fontVariantNumeric: 'tabular-nums', minWidth: 16, textAlign: 'center' }}>{fontSize}</span>
          <span style={{ cursor: 'pointer', fontSize: 15, opacity: 0.5, padding: '4px', fontWeight: 500, color: textColor }} onClick={() => setFontSize(Math.min(24, fontSize + 1))}>A+</span>
          <span style={{ width: 1, height: 14, background: darkMode ? '#444' : '#ddd', opacity: 0.5 }} />
          <span style={{ cursor: 'pointer', fontSize: 14, opacity: 0.5, padding: '4px' }} onClick={() => setDarkMode(!darkMode)}>
            {darkMode ? '☀️' : '🌙'}
          </span>
          <span style={{ width: 1, height: 14, background: darkMode ? '#444' : '#ddd', opacity: 0.5 }} />
          <span style={{ cursor: 'pointer', fontSize: 14, opacity: 0.5, padding: '4px' }} onClick={() => setShowNoteInput(!showNoteInput)}>
            ✏️
          </span>
        </div>
      </div>
      )}
    </div>
  );
}

export default Reader;